import { useEffect, useMemo, useState } from "react";

import type { CaptainBillingHistoryEntry, CaptainBillingHistorySource } from "../../types";

type NewEntryKeys = {
  shownSource: CaptainBillingHistorySource | undefined;
  isNew: (entry: CaptainBillingHistoryEntry) => boolean;
};

type Seen = { source: CaptainBillingHistorySource | undefined; keys: Set<string> };

const entryKey = (entry: Pick<CaptainBillingHistoryEntry, "version" | "occurredAt">) => `${entry.version}:${entry.occurredAt}`;

export function useNewEntryKeys(
  rows: CaptainBillingHistoryEntry[],
  source: CaptainBillingHistorySource | undefined,
  settled: boolean,
): NewEntryKeys {
  const [shownSource, setShownSource] = useState(source);
  const [seen, setSeen] = useState<Seen | null>(null);
  const keys = useMemo(() => rows.map(entryKey), [rows]);

  if (settled && shownSource !== source) setShownSource(source);
  if (settled && (!seen || seen.source !== source)) setSeen({ source, keys: new Set(keys) });

  const tracking = !!seen && seen.source === shownSource;
  const unseen = useMemo(() => (tracking ? keys.filter((key) => !seen.keys.has(key)) : []), [tracking, keys, seen]);

  useEffect(() => {
    if (unseen.length === 0) return;
    setSeen((current) => current && { ...current, keys: new Set([...current.keys, ...unseen]) });
  }, [unseen]);

  return { shownSource, isNew: (entry) => unseen.includes(entryKey(entry)) };
}
